import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTimeclockStore, selectDaySession, selectReviewSession } from '../../../storeTimeclock';
import PWatchdogDialog from './dialogs/PWatchdogDialog';

/**
 * App-wide watchers, mounted once (Dashboard). Both are driven by server truth from polling:
 *  - P-watchdog when a polled session carries needs_review (watchdog task flagged it);
 *  - the auto-stop toast when a running day session becomes closed with an auto end_reason.
 * Nothing here is inferred from local actions.
 */
export default function ClockWatchers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const review = useTimeclockStore(selectReviewSession);
  const daySession = useTimeclockStore(selectDaySession);
  const acknowledgeReview = useTimeclockStore((s) => s.acknowledgeReview);
  const clockOut = useTimeclockStore((s) => s.clockOut);
  const fetchDay = useTimeclockStore((s) => s.fetchDay);

  const [autoStopOpen, setAutoStopOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const prevHadDay = useRef(false);

  // Auto-stop toast: tied to the closed day session's end_reason (server truth), not a local guess.
  useEffect(() => {
    const hasDay = !!daySession;
    if (prevHadDay.current && !hasDay) {
      (async () => {
        const d = await fetchDay();
        const closedDays = (d?.sessions || [])
          .filter((s) => s.kind === 'day' && s.ended_at)
          .sort((a, b) => new Date(b.ended_at) - new Date(a.ended_at));
        const latest = closedDays[0];
        if (latest && ['auto_day_end', 'watchdog'].includes(latest.end_reason)) {
          setAutoStopOpen(true);
        }
      })();
    }
    prevHadDay.current = hasDay;
  }, [daySession, fetchDay]);

  const handleStop = async () => {
    if (!review) return;
    setBusy(true);
    try {
      await clockOut(review.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PWatchdogDialog
        open={!!review}
        durationSeconds={review?.duration_seconds || 0}
        busy={busy}
        onContinue={() => review && acknowledgeReview(review.id)}
        onStop={handleStop}
      />
      <Snackbar
        open={autoStopOpen}
        autoHideDuration={10000}
        onClose={() => setAutoStopOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setAutoStopOpen(false)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setAutoStopOpen(false);
                navigate('/time-clock');
              }}
            >
              {t('worktime.autoStop.review')}
            </Button>
          }
          sx={{ width: '100%' }}
        >
          {t('worktime.autoStop.message')}
        </Alert>
      </Snackbar>
    </>
  );
}
